import { type ToastActionElement } from "@/components/ui/toast";
import * as React from "react";

// Define interfaces
const TOAST_LIMIT = 3;
const TOAST_REMOVE_DELAY = 5000;

// Modified ToasterToast to not extend ToastProps to avoid type conflicts
interface ToasterToast {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
  variant?: "default" | "destructive";
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  duration?: number; // Individuelle Dauer in Millisekunden
}

interface ToasterProps {
  data: ToasterToast[];
}

// Define state types
type State = {
  toasts: ToasterToast[];
};

// Define actions
type Action =
  | { type: "ADD_TOAST"; toast: ToasterToast }
  | { type: "UPDATE_TOAST"; toast: Partial<ToasterToast>; id: string }
  | { type: "DISMISS_TOAST"; id: string }
  | { type: "REMOVE_TOAST"; id: string };

// Generate ID
const genId = (len: number = 8): string => {
  let result = "";
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  for (let i = 0; i < len; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

// Reducer
const toastReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map(t =>
          t.id === action.id ? { ...t, ...action.toast } : t
        ),
      };

    case "DISMISS_TOAST": {
      const { id } = action;

      return {
        ...state,
        toasts: state.toasts.map(t =>
          t.id === id
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      };
    }

    case "REMOVE_TOAST":
      if (action.id === undefined) {
        return {
          ...state,
          toasts: [],
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter(t => t.id !== action.id),
      };
  }
};

const listeners: Array<(state: State) => void> = [];

let memoryState: State = { toasts: [] };

function dispatch(action: Action) {
  memoryState = toastReducer(memoryState, action);
  listeners.forEach(listener => {
    listener(memoryState);
  });
}

// Create toast
export type Toast = Omit<ToasterToast, "id" | "open" | "onOpenChange">;

// Add helper methods for common toast types
function createToast(props: Toast) {
  const id = genId();

  const update = (props: Partial<ToasterToast>) =>
    dispatch({
      type: "UPDATE_TOAST",
      id,
      toast: props,
    });

  const dismiss = () => dispatch({ type: "DISMISS_TOAST", id });

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: open => {
        if (!open) dismiss();
      },
    },
  });

  // Automatisches Schließen nach individueller Dauer oder Standard-Dauer
  const duration = props.duration ?? TOAST_REMOVE_DELAY;
  setTimeout(() => {
    dismiss();
  }, duration);

  return {
    id,
    dismiss,
    update,
  };
}

// Updated toast object structure to include a callable function
interface ToastFunctions {
  (props: Toast): ReturnType<typeof createToast>;
  custom: (props: Toast) => ReturnType<typeof createToast>;
  success: (message: string) => ReturnType<typeof createToast>;
  error: (message: string) => ReturnType<typeof createToast>;
  info: (message: string) => ReturnType<typeof createToast>;
  warning: (message: string) => ReturnType<typeof createToast>;
}

// Create the callable toast function along with methods
const toast: ToastFunctions = ((props: Toast) =>
  createToast(props)) as ToastFunctions;

// Add helper methods to the callable toast function
toast.custom = (props: Toast) => createToast(props);
toast.success = (message: string) =>
  createToast({
    title: "Erfolg",
    description: message,
    variant: "default",
  });
toast.error = (message: string) =>
  createToast({
    title: "Fehler",
    description: message,
    variant: "destructive",
  });
toast.info = (message: string) =>
  createToast({
    title: "Info",
    description: message,
    variant: "default",
  });
toast.warning = (message: string) =>
  createToast({
    title: "Warnung",
    description: message,
    variant: "default",
  });

function useToast() {
  const [state, setState] = React.useState<State>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);

  return {
    ...state,
    toast,
    dismiss: (id: string) => dispatch({ type: "DISMISS_TOAST", id }),
  };
}

export { toast, useToast };
